import { Divider, Box, Flex } from '@cad-arc';
import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { UnitOverview } from './unit-overview';
import { LocationAddressAndGpsSection } from '../location-address-and-gps-section';
import { UnitInfoSection } from './unit-info-section';
import { UnitAssignedSection } from './unit-assigned-section';
import { HospitalStatusSection } from '../hospital-panel/hospital-status-section';
import { getMapViewportUnitLocationSource } from '../../../../esri/helpers/locations';
import { EsriMapEntityTypeEnum, UnitLocationSource } from '../../../../esri/types';
import { PanelWrapper } from '../panel-wrapper';
import { getEventIcon } from '../helpers';
import { getEventPriorityAttributeId } from '../../../../esri/helpers/map-icon-theme';
import { UnitPanelInitiateButton } from './unit-panel-initiate-button';
import { SelfInitiateButton } from '../self-initiate-button';
import { useUnitContextMenuQuery } from '../../../../unit-context-menu/use-unit-context-menu-query';
import { useUnitAssignment } from '../../../../unit-assignment/unit-assignment-context';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { isValidLatLng } from '../../../../location/helpers';
import { OpenSensorGridButton } from '../../../../sensors/open-sensor-grid-button';
import { useSensorsEnabled } from '../../../../sensors/use-sensors-enabled';

export const UnitPanel = observer(function UnitPanel(): React.ReactElement | null {
    const mapStore = useEsriMapStore();
    const {
        showUnitLocationWhenGpsIsNotAvailable,
        dispatchFromMap,
        selfInitiateEvent,
    } = useEnabledFeatures();
    const sensorsEnabled = useSensorsEnabled();
    const { isFirstResponder, isCad } = useApplicationType();
    const unitAssignment = useUnitAssignment();

    const isMapLedgerUserEntity =
        mapStore.selectedMapLedgerEntityType === EsriMapEntityTypeEnum.USER;

    const initiateOptionEnabled =
        isCad && dispatchFromMap && !isMapLedgerUserEntity && !!mapStore.selectedUnitId;

    const { data: unitContextMenuView, refetch } = useUnitContextMenuQuery({
        unitId: mapStore.selectedUnitId!,
        enabled: initiateOptionEnabled,
    });

    useEffect(
        function refetchUnitContextMenuQuery() {
            refetch();
        },
        [mapStore.selectedMapLedgerUnit?.assignedEventId, refetch]
    );

    const size = isFirstResponder ? 'lg' : 'md';

    const showInitiateButtonInFooter =
        initiateOptionEnabled && unitContextMenuView?.initiateSubmenuView?.isVisible;

    const usersUnitId = unitAssignment?.unitId;
    const isUsersOwnUnitSelected = usersUnitId === mapStore.selectedUnitId;
    const isAssignedToEvent = !!unitAssignment?.assignedEvent;

    const showSelfInitiateButtonInFooter =
        isFirstResponder && selfInitiateEvent && isUsersOwnUnitSelected && !isAssignedToEvent;

    if (!mapStore) {
        return null;
    }
    const selectedMapLedgerUnit = mapStore.selectedMapLedgerUnit;
    let gpsLocation;
    let geoLocation;
    let unitLocationSource: UnitLocationSource | undefined = 'GPS';

    if (isMapLedgerUserEntity && mapStore.selectedUnitUserId) {
        const unitId = mapStore.getUnitIdByUserId(mapStore.selectedUnitUserId);
        const selectedViewportUnit = unitId ? mapStore.getMapViewportUnit(unitId) : undefined;
        gpsLocation = selectedViewportUnit?.unitUserLocations?.find(
            (unitUserLocation) => unitUserLocation.entityId === mapStore.selectedUnitUserId
        )?.gpsLocation;
    } else {
        if (mapStore.selectedUnitId) {
            const mapViewportUnit = mapStore.getMapViewportUnit(mapStore.selectedUnitId);
            gpsLocation = mapViewportUnit?.gpsLocation;
            geoLocation = mapViewportUnit?.geoLocation;
            unitLocationSource = getMapViewportUnitLocationSource(
                mapViewportUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );
        }
    }

    const {
        placeName,
        displayLineFirst,
        displayLineSubPremise,
        displayLineOptionalMiddle,
        displayLineLast,
        areCoordinatesVerified,
        locationSource,
        assignedEventId,
        assignedAgencyEventNumber,
        assignedEventPriority,
        eventLocationNotes,
        eventNarrative,
        cfsCode,
        cfsDescription,
        eventPlaceName,
        eventLocationSource,
        eventAreCoordinatesVerified,
        eventDisplayLineFirst,
        eventDisplayLineLast,
        eventDisplayLineOptionalMiddle,
        eventDisplayLineSubPremise,
        hospitalId,
    } = selectedMapLedgerUnit || {};
    const hospital = hospitalId ? mapStore.getMapLedgerHospital(hospitalId) : undefined;
    const assignedMapViewportEvent = assignedEventId
        ? mapStore.getMapViewportEvent(assignedEventId)
        : undefined;

    /*
     * Where the unit actually is. GPS first and the geo location as the fallback, matching what
     * the panel above displays — `unitLocationSource` is the same choice made for the address
     * section, so the grid measures from the position the dispatcher can see.
     */
    const unitLocation = unitLocationSource === 'GEO' ? geoLocation : gpsLocation;
    const showOpenSensorGridButton =
        sensorsEnabled && isValidLatLng(unitLocation?.latitude, unitLocation?.longitude);

    const initiateButton = showInitiateButtonInFooter ? (
        <UnitPanelInitiateButton
            initiateSubmenuView={unitContextMenuView.initiateSubmenuView}
            clickParams={unitContextMenuView.clickParams}
        />
    ) : showSelfInitiateButtonInFooter ? (
        <SelfInitiateButton unitId={usersUnitId} />
    ) : null;

    // A row only when the sensor grid button is there too. Without it the footer is exactly what it
    // was before sensors: the initiate button on its own, centred by the footer.
    const footerContent = showOpenSensorGridButton ? (
        // The footer is a fixed-height centred row, so the buttons need their own gap.
        <Flex gap={2} width="100%" alignItems="center">
            {/* Wrapped only when present, so an absent initiate button claims no row space. */}
            {initiateButton ? (
                <Box flexGrow={1} flexBasis={0} minWidth={0}>
                    {initiateButton}
                </Box>
            ) : undefined}
            <OpenSensorGridButton
                latitude={unitLocation?.latitude}
                longitude={unitLocation?.longitude}
            />
        </Flex>
    ) : (
        initiateButton
    );

    return (
        <PanelWrapper testId="MAP_UNIT_PANEL" footerContent={footerContent}>
            <UnitOverview />
            {unitLocationSource === 'GEO' ? (
                <LocationAddressAndGpsSection
                    isGpsLocation={false}
                    isUserLocation={isMapLedgerUserEntity}
                    shouldSearchReverseGeocodeLocation={false}
                    placeName={placeName}
                    source={locationSource}
                    displayLineFirst={displayLineFirst}
                    displayLineSubPremises={displayLineSubPremise}
                    displayLineOptionalMiddle={displayLineOptionalMiddle}
                    displayLineLast={displayLineLast}
                    geoLocationLatitude={geoLocation?.latitude}
                    geoLocationLongitude={geoLocation?.longitude}
                    geoLocationTimestamp={geoLocation?.timestamp}
                    areGeoLocationCoordinatesVerified={areCoordinatesVerified}
                    size={size}
                />
            ) : (
                <LocationAddressAndGpsSection
                    isGpsLocation={true}
                    isUserLocation={isMapLedgerUserEntity}
                    shouldSearchReverseGeocodeLocation={isMapLedgerUserEntity}
                    gpsLocationTimestamp={gpsLocation?.timestamp}
                    gpsLocationLatitude={gpsLocation?.latitude}
                    gpsLocationLongitude={gpsLocation?.longitude}
                    size={size}
                />
            )}

            <UnitInfoSection />
            {!!hospital && (
                <>
                    <LocationAddressAndGpsSection
                        locationSectionTitle="Hospital Info"
                        locationSectionSubTitle={hospital.name}
                        geoLocationLatitude={hospital.geoLocation?.latitude}
                        geoLocationLongitude={hospital.geoLocation?.longitude}
                        areGeoLocationCoordinatesVerified={hospital.areCoordinatesVerified}
                        placeName={hospital.placeName}
                        displayLineFirst={hospital.displayLineFirst}
                        displayLineLast={hospital.displayLineLast}
                        displayLineOptionalMiddle={hospital.displayLineOptionalMiddle}
                        displayLineSubPremises={hospital.displayLineSubPremise}
                        source={hospital.locationSource}
                        showSectionDivider={false}
                        size={size}
                    />
                    <Box marginLeft={18} marginRight={18}>
                        <Divider />
                    </Box>
                    <HospitalStatusSection
                        status={hospital.status}
                        statusGlobalAttributeId={hospital.statusGlobalAttributeId}
                        lastUpdatedDateUtc={hospital.lastUpdatedDateUtc}
                        capabilities={hospital.capabilities}
                        notes={hospital.notes}
                    />
                </>
            )}
            {!!assignedEventId && (
                <UnitAssignedSection
                    eventId={assignedEventId}
                    eventIcon={getEventIcon(
                        true,
                        getEventPriorityAttributeId(assignedMapViewportEvent),
                        assignedMapViewportEvent?.agencyTypeGlobalAttrId
                    )}
                    eventNumber={assignedAgencyEventNumber!}
                    eventPriority={assignedEventPriority!}
                    eventLocationNotes={eventLocationNotes}
                    eventNarrative={eventNarrative}
                    cfsCode={cfsCode!}
                    cfsDescription={cfsDescription!}
                    placeName={eventPlaceName}
                    locationSource={eventLocationSource}
                    isVerifiedLocation={eventAreCoordinatesVerified}
                    displayLineFirst={eventDisplayLineFirst}
                    displayLineLast={eventDisplayLineLast}
                    displayLineOptionalMiddle={eventDisplayLineOptionalMiddle}
                    eventDisplayLineSubPremise={eventDisplayLineSubPremise}
                    size={size}
                />
            )}
        </PanelWrapper>
    );
});
